import React, { useState, useEffect } from "react";
import apiClient from "../api/client";
import { useTrackedStocks } from "../hooks/useTrackedStocks";


export default function StockWidget() {
  const [open, setOpen] = useState(false);
  const [stocksData, setStocksData] = useState({});
  const [loading, setLoading] = useState(true);
  const { stocks: trackedStocks, loading: trackedLoading } = useTrackedStocks();

  useEffect(() => {
    if (trackedLoading || trackedStocks.length === 0) return;
    let cancelled = false;
    const fetches = trackedStocks.map((s) =>
      apiClient
        .get(`/api/rates/stock/${s.symbol}`)
        .then((r) => ({ symbol: s.symbol, data: r.data, error: null }))
        .catch((e) => ({ symbol: s.symbol, data: null, error: e.message || "שגיאה" }))
    );
    Promise.all(fetches).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach((r) => { map[r.symbol] = { data: r.data, error: r.error }; });
      setStocksData(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [trackedStocks, trackedLoading]);

  // Check if any stock has data to show
  const hasData = trackedStocks.some((s) => stocksData[s.symbol]?.data?.price != null);

  // Calculate portfolio-wide sentiment for trigger badge
  const overallUp = trackedStocks.filter((s) => {
    const d = stocksData[s.symbol]?.data;
    if (!d) return false;
    return (d.changePct || 0) >= 0;
  }).length;
  const overallDown = trackedStocks.filter((s) => {
    const d = stocksData[s.symbol]?.data;
    if (!d) return false;
    return (d.changePct || 0) < 0;
  }).length;
  const sentiment = overallUp >= overallDown ? "up" : "down";

  return (
    <div style={widgetStyles.root}>
      {/* Expanded panel */}
      {open && (
        <div className="stock-widget-panel" style={widgetStyles.panel}>
          <div style={widgetStyles.panelHeader}>
            <span style={widgetStyles.panelTitle}>מניות</span>
            <button
              style={widgetStyles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="סגור"
            >
              ×
            </button>
          </div>
          <div style={widgetStyles.stockList}>
            {(loading || trackedLoading) && (
              <div style={widgetStyles.loading}>טוען...</div>
            )}
            {!loading && !trackedLoading && trackedStocks.map((s) => {
              const info = stocksData[s.symbol];
              const d = info?.data;
              const price = d?.price;
              const changePct = d?.changePct;
              const isUp = (changePct || 0) >= 0;
              const currency = d?.currency || "ILS";

              return (
                <div key={s.symbol} style={widgetStyles.stockItem}>
                  <div style={widgetStyles.stockLeft}>
                    <span className="stock-item-symbol" style={widgetStyles.stockSymbol}>
                      {s.name}
                    </span>
                    <span style={widgetStyles.stockCode}>{s.symbol}</span>
                  </div>
                  <div style={widgetStyles.stockRight}>
                    {info?.error && (
                      <span style={widgetStyles.stockNA}>—</span>
                    )}
                    {!info?.error && price != null && (
                      <>
                        <span className="stock-item-price" style={widgetStyles.stockPrice}>
                          {new Intl.NumberFormat("he-IL", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)}
                        </span>
                        {changePct != null && (
                          <span style={{
                            ...widgetStyles.stockChange,
                            color: isUp ? "#22c55e" : "#ef4444",
                          }}>
                            {isUp ? "▲" : "▼"} {Math.abs(changePct).toFixed(2)}%
                          </span>
                        )}
                      </>
                    )}
                    {!info?.error && price == null && !loading && (
                      <span style={widgetStyles.stockNA}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!loading && (
            <div style={widgetStyles.panelFooter}>
              <span style={widgetStyles.footerNote}>נתונים חיים / שער סגירה • Yahoo Finance</span>
            </div>
          )}
        </div>
      )}

      {/* Trigger button */}
      <button
        className="stock-widget-trigger"
        style={{
          ...widgetStyles.trigger,
          ...(open ? widgetStyles.triggerOpen : {}),
        }}
        onClick={() => setOpen((v) => !v)}
        aria-label="מניות"
        title="הצג מניות"
      >
        <span style={widgetStyles.triggerIcon}>
          {loading ? "⏳" : hasData ? (sentiment === "up" ? "📈" : "📉") : "📊"}
        </span>
        {!loading && hasData && (
          <span style={{
            ...widgetStyles.triggerDot,
            background: sentiment === "up" ? "#22c55e" : "#ef4444",
          }} />
        )}
      </button>
    </div>
  );
}

const widgetStyles = {
  root: {
    position: "fixed",
    bottom: "24px",
    right: "24px",   /* right in RTL = left side visually */
    zIndex: 500,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px",
  },
  panel: {
    width: "280px",
    borderRadius: "14px",
    overflow: "hidden",
    /* background and border come from theme CSS */
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px 10px",
    borderBottom: "1px solid rgba(128,128,128,0.12)",
  },
  panelTitle: {
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: "var(--text)",
  },
  closeBtn: {
    width: "24px",
    height: "24px",
    border: "none",
    background: "transparent",
    borderRadius: "50%",
    fontSize: "18px",
    lineHeight: "1",
    cursor: "pointer",
    color: "var(--text-secondary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--font)",
    padding: 0,
  },
  stockList: {
    padding: "8px 0",
    maxHeight: "280px",
    overflowY: "auto",
  },
  loading: {
    padding: "16px",
    textAlign: "center",
    fontSize: "13px",
    color: "var(--text-secondary)",
  },
  stockItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    transition: "background 0.12s",
    cursor: "default",
  },
  stockLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  stockRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
  },
  stockSymbol: {
    fontSize: "13px",
    fontWeight: 600,
    /* color comes from theme */
  },
  stockCode: {
    fontSize: "11px",
    color: "var(--text-secondary)",
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
  },
  stockPrice: {
    fontSize: "13px",
    fontWeight: 600,
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
    /* color comes from theme */
  },
  stockChange: {
    fontSize: "11px",
    fontWeight: 500,
  },
  stockNA: {
    fontSize: "13px",
    color: "var(--text-secondary)",
  },
  panelFooter: {
    padding: "8px 16px 12px",
    borderTop: "1px solid rgba(128,128,128,0.1)",
  },
  footerNote: {
    fontSize: "11px",
    color: "var(--text-secondary)",
  },
  trigger: {
    position: "relative",
    width: "48px",
    height: "48px",
    border: "1px solid var(--border-light)",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    /* background comes from theme */
  },
  triggerOpen: {
    transform: "scale(0.95)",
  },
  triggerIcon: {
    fontSize: "22px",
    lineHeight: 1,
  },
  triggerDot: {
    position: "absolute",
    top: "4px",
    left: "4px",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: "2px solid var(--bg)",
  },
};
